import React from "react";
import { Link } from "react-router-dom";
import CountdownTimer from "./CountdownTimer";
import { formatBRL, statusColor, statusLabel } from "@/lib/constants";
import { MapPin, Package, Sparkles, Star, ShieldCheck } from "lucide-react";
import { MONETIZATION_ENABLED } from "@/lib/features";

const ORDER_TYPE_META = {
  compra: null,
  permuta: { label: "Permuta", icon: "🔄", cls: "bg-blue-500/20 text-blue-400 border-blue-500/40" },
  doacao: { label: "Doação", icon: "🎁", cls: "bg-pink-500/20 text-pink-400 border-pink-500/40" },
  servico: { label: "Serviço", icon: "🛠", cls: "bg-emerald-500/20 text-emerald-400 border-emerald-500/40" },
};

function BoostBadge({ orderId }) {
  return (
    <div
      data-testid={`boost-badge-${orderId}`}
      className="inline-flex items-center gap-1 mb-2 text-[10px] uppercase tracking-widest font-bold bg-primary text-primary-foreground px-2 py-1 rounded-full"
    >
      <Star size={10} strokeWidth={3} className="fill-current" /> Em destaque
    </div>
  );
}

function TypeBadge({ orderId, meta }) {
  return (
    <div
      data-testid={`type-badge-${orderId}`}
      className={`inline-flex items-center gap-1 mb-2 ml-1 text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded-full border ${meta.cls}`}
    >
      <span>{meta.icon}</span> {meta.label}
    </div>
  );
}

function OrderValueField({ order }) {
  const label = order.order_type === "compra"
    ? "Valor máx."
    : order.order_type === "permuta" ? "Oferece" : "Tipo";
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">{label}</p>
      {order.order_type === "compra" && (
        <p className="font-mono font-bold text-primary text-lg">{formatBRL(order.max_price)}</p>
      )}
      {order.order_type === "permuta" && (
        <p className="text-sm font-medium truncate">{order.offered_item || "—"}</p>
      )}
      {order.order_type === "doacao" && (
        <p className="text-sm font-medium">
          {order.donation_direction === "dar" ? "Ofereço doação" : "Preciso de doação"}
        </p>
      )}
    </div>
  );
}

function AiHint({ order }) {
  if (!order.ai_suggested_price || Math.abs(order.ai_suggested_price - order.max_price) <= 1) return null;
  return (
    <div className="flex items-center gap-1.5 text-xs text-blue-500 mb-3">
      <Sparkles size={12} />
      <span>IA sugere preço médio: {formatBRL(order.ai_suggested_price)}</span>
    </div>
  );
}

function FooterRow({ order, actionLabel }) {
  return (
    <div className="flex items-center gap-3 text-xs text-muted-foreground border-t border-border pt-3">
      <span className="flex items-center gap-1">
        <Package size={12} /> {order.condition} · {order.quantity}
      </span>
      {order.distance_km != null ? (
        <span className="flex items-center gap-1 text-primary font-medium">
          <MapPin size={12} /> {order.distance_km} km
        </span>
      ) : (
        <span className="flex items-center gap-1">
          <MapPin size={12} /> {order.max_distance_km} km
        </span>
      )}
      {order.status === "accepted" && order.accept_expires_at && (
        <span className="ml-auto flex items-center gap-1 text-amber-500">
          <CountdownTimer target={order.accept_expires_at} testId={`card-timer-${order.order_id}`} />
        </span>
      )}
      {order.status === "open" && (
        <span className="ml-auto font-medium text-primary group-hover:underline">
          {actionLabel} →
        </span>
      )}
    </div>
  );
}

export default function OrderCard({ order, actionLabel = "Ver pedido", testId }) {
  const typeMeta = ORDER_TYPE_META[order.order_type || "compra"];
  const cover = Array.isArray(order.photos) && order.photos.length > 0 ? order.photos[0] : null;
  const showBoost = MONETIZATION_ENABLED && order.is_boosted;
  return (
    <Link
      to={`/orders/${order.order_id}`}
      data-testid={testId || `order-card-${order.order_id}`}
      className="block group"
    >
      <article className={`bg-card border rounded-xl overflow-hidden transition-all hover:-translate-y-0.5 ${
        showBoost
          ? "border-primary shadow-lg shadow-primary/20 ring-1 ring-primary/40"
          : "border-border hover:border-primary/50"
      }`}>
        {cover && (
          <div
            data-testid={`order-cover-${order.order_id}`}
            className="relative aspect-[16/9] w-full bg-secondary overflow-hidden"
          >
            <img
              src={cover}
              alt={order.title}
              loading="lazy"
              className="w-full h-full object-cover transition-transform group-hover:scale-[1.03]"
            />
            {order.photos.length > 1 && (
              <span className="absolute bottom-2 right-2 bg-black/70 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                +{order.photos.length - 1}
              </span>
            )}
          </div>
        )}
        <div className="p-5">
        {showBoost && <BoostBadge orderId={order.order_id} />}
        {typeMeta && <TypeBadge orderId={order.order_id} meta={typeMeta} />}
        {order.moderation_decision === "approve" && order.status === "open" && (
          <div
            data-testid={`approved-badge-${order.order_id}`}
            className="inline-flex items-center gap-1 mb-2 ml-1 text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded-full border border-emerald-500/40 bg-emerald-500/10 text-emerald-500"
            title="Este pedido passou pela moderação"
          >
            <ShieldCheck size={10} strokeWidth={3} /> Aprovado
          </div>
        )}

        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="min-w-0 flex-1">
            <h3 className="font-heading font-bold text-lg leading-tight truncate">{order.title}</h3>
            <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1 truncate">
              <span className="truncate">{order.buyer_name}</span>
              {order.buyer_verified && (
                <ShieldCheck
                  size={12}
                  className="text-emerald-500 shrink-0"
                  data-testid={`buyer-verified-${order.order_id}`}
                />
              )}
              <span className="truncate">• {order.city}</span>
            </p>
          </div>
          <span
            className={`shrink-0 text-[10px] uppercase tracking-wider font-bold border px-2 py-1 rounded-md ${statusColor(order.status)}`}
          >
            {statusLabel(order.status)}
          </span>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-3">
          <OrderValueField order={order} />
          <div>
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Categoria</p>
            <p className="font-medium text-sm truncate">{order.category || "—"}</p>
          </div>
        </div>

        <AiHint order={order} />
        <FooterRow order={order} actionLabel={actionLabel} />
        </div>
      </article>
    </Link>
  );
}
